
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * AgentInfo struct
 */
class AgentInfo {
  /**
   * Initialize struct
   * @param {!string=} uniqueId
   * @param {!string=} application
   * @param {!string=} appVersion
   * @param {!string=} device
   * @param {!string=} browser
   * @param {!string=} oS
   * @param {!string=} oSVersion
   * @constructor
   */
  constructor (uniqueId = '', application = '', appVersion = '', device = '', browser = '', oS = '', oSVersion = '') {
    this.UniqueId = uniqueId
    this.Application = application
    this.AppVersion = appVersion
    this.Device = device
    this.Browser = browser
    this.OS = oS
    this.OSVersion = oSVersion
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AgentInfo}
   * @param {!AgentInfo} other Other struct
   * @returns {!AgentInfo} This struct
   */
  copy (other) {
    if (other.UniqueId != null) {
      this.UniqueId = other.UniqueId
    } else {
      this.UniqueId = null
    }
    if (other.Application != null) {
      this.Application = other.Application
    } else {
      this.Application = null
    }
    if (other.AppVersion != null) {
      this.AppVersion = other.AppVersion
    } else {
      this.AppVersion = null
    }
    if (other.Device != null) {
      this.Device = other.Device
    } else {
      this.Device = null
    }
    if (other.Browser != null) {
      this.Browser = other.Browser
    } else {
      this.Browser = null
    }
    if (other.OS != null) {
      this.OS = other.OS
    } else {
      this.OS = null
    }
    if (other.OSVersion != null) {
      this.OSVersion = other.OSVersion
    } else {
      this.OSVersion = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AgentInfo}
   * @returns {!AgentInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AgentInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AgentInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AgentInfo}
   * @param {!AgentInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AgentInfo)) {
      throw new TypeError('Instance of AgentInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AgentInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      UniqueId: ((this.UniqueId != null) ? this.UniqueId : null),
      Application: ((this.Application != null) ? this.Application : null),
      AppVersion: ((this.AppVersion != null) ? this.AppVersion : null),
      Device: ((this.Device != null) ? this.Device : null),
      Browser: ((this.Browser != null) ? this.Browser : null),
      OS: ((this.OS != null) ? this.OS : null),
      OSVersion: ((this.OSVersion != null) ? this.OSVersion : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AgentInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AgentInfo} other Object value
   * @returns {!AgentInfo} Created struct
   */
  static fromObject (other) {
    return new AgentInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AgentInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AgentInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AgentInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 13019
  }
}

export { AgentInfo };

/**
 * Fast Binary Encoding AgentInfo field model
 */
class FieldModelAgentInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.UniqueId = new fbe.FieldModelString(buffer, 4 + 4)
    this.Application = new fbe.FieldModelString(buffer, this.UniqueId.fbeOffset + this.UniqueId.fbeSize)
    this.AppVersion = new fbe.FieldModelString(buffer, this.Application.fbeOffset + this.Application.fbeSize)
    this.Device = new fbe.FieldModelString(buffer, this.AppVersion.fbeOffset + this.AppVersion.fbeSize)
    this.Browser = new fbe.FieldModelString(buffer, this.Device.fbeOffset + this.Device.fbeSize)
    this.OS = new fbe.FieldModelString(buffer, this.Browser.fbeOffset + this.Browser.fbeSize)
    this.OSVersion = new fbe.FieldModelString(buffer, this.OS.fbeOffset + this.OS.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelAgentInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAgentInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.UniqueId.fbeSize + this.Application.fbeSize + this.AppVersion.fbeSize + this.Device.fbeSize + this.Browser.fbeSize + this.OS.fbeSize + this.OSVersion.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAgentInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.UniqueId.fbeExtra + this.Application.fbeExtra + this.AppVersion.fbeExtra + this.Device.fbeExtra + this.Browser.fbeExtra + this.OS.fbeExtra + this.OSVersion.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAgentInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAgentInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAgentInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 13019
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAgentInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAgentInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UniqueId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UniqueId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UniqueId.fbeSize

    if ((fbeCurrentSize + this.Application.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Application.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Application.fbeSize

    if ((fbeCurrentSize + this.AppVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AppVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AppVersion.fbeSize

    if ((fbeCurrentSize + this.Device.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Device.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Device.fbeSize

    if ((fbeCurrentSize + this.Browser.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Browser.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Browser.fbeSize

    if ((fbeCurrentSize + this.OS.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OS.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OS.fbeSize

    if ((fbeCurrentSize + this.OSVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OSVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OSVersion.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAgentInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAgentInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAgentInfo}
   * @param {!AgentInfo} fbeValue Default value, defaults is new AgentInfo()
   * @returns {!AgentInfo} AgentInfo value
   */
  get (fbeValue = new AgentInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAgentInfo}
   * @param {!AgentInfo} fbeValue AgentInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UniqueId.fbeSize) <= fbeStructSize) {
      fbeValue.UniqueId = this.UniqueId.get()
    } else {
      fbeValue.UniqueId = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UniqueId.fbeSize

    if ((fbeCurrentSize + this.Application.fbeSize) <= fbeStructSize) {
      fbeValue.Application = this.Application.get()
    } else {
      fbeValue.Application = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Application.fbeSize

    if ((fbeCurrentSize + this.AppVersion.fbeSize) <= fbeStructSize) {
      fbeValue.AppVersion = this.AppVersion.get()
    } else {
      fbeValue.AppVersion = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AppVersion.fbeSize

    if ((fbeCurrentSize + this.Device.fbeSize) <= fbeStructSize) {
      fbeValue.Device = this.Device.get()
    } else {
      fbeValue.Device = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Device.fbeSize

    if ((fbeCurrentSize + this.Browser.fbeSize) <= fbeStructSize) {
      fbeValue.Browser = this.Browser.get()
    } else {
      fbeValue.Browser = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Browser.fbeSize

    if ((fbeCurrentSize + this.OS.fbeSize) <= fbeStructSize) {
      fbeValue.OS = this.OS.get()
    } else {
      fbeValue.OS = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OS.fbeSize

    if ((fbeCurrentSize + this.OSVersion.fbeSize) <= fbeStructSize) {
      fbeValue.OSVersion = this.OSVersion.get()
    } else {
      fbeValue.OSVersion = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OSVersion.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAgentInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAgentInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAgentInfo}
   * @param {!AgentInfo} fbeValue AgentInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAgentInfo}
   * @param {AgentInfo} fbeValue AgentInfo value
   */
  setFields (fbeValue) {
    this.UniqueId.set(fbeValue.UniqueId)
    this.Application.set(fbeValue.Application)
    this.AppVersion.set(fbeValue.AppVersion)
    this.Device.set(fbeValue.Device)
    this.Browser.set(fbeValue.Browser)
    this.OS.set(fbeValue.OS)
    this.OSVersion.set(fbeValue.OSVersion)
  }
}

export { FieldModelAgentInfo };

/**
 * Fast Binary Encoding AgentInfo model
 */
class AgentInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAgentInfo(this.buffer, 4)
  }

  /**
   * Get the AgentInfo model
   * @this {!AgentInfoModel}
   * @returns {!FieldModelAgentInfo} model AgentInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AgentInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AgentInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AgentInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AgentInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAgentInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AgentInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AgentInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AgentInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AgentInfoModel}
   * @param {!AgentInfo} value AgentInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AgentInfoModel}
   * @param {!AgentInfo} value AgentInfo value, defaults is new AgentInfo()
   * @return {!object} Deserialized AgentInfo value and its size
   */
  deserialize (value = new AgentInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AgentInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AgentInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AgentInfoModel}
   * @param {!number} prev Previous AgentInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AgentInfoModel };
